/**
 * Single, kind-parameterized admin workspace for the unified paint
 * catalog. Mounted at:
 *   - /admin/system-catalog (kind="tile")
 *   - /admin/overlays       (kind="overlay")
 *
 * Backed by the unified `/api/admin/paint-catalog` API surface
 * (`packages/backend/src/routes/admin/paintCatalog.ts`) and the
 * `paintCatalogRepository` repo on the backend.
 *
 * Provides:
 *   - Folder tree with rename / availability / delete and child-folder add
 *   - Paint set list per folder with multi-select bulk actions
 *     (merge, move, make available/unavailable/inherit)
 *   - Per-set: paint mode toggle, availability, split-into-singletons,
 *     delete, plus an expandable assets panel for per-asset CRUD
 *   - Native HTML5 drag-and-drop: drag selected sets onto a folder
 *     row in the tree to bulk-move them
 *   - Source/availability badges and protection of `source = system`
 *     items (protected mutations are surfaced as toast errors)
 */

import { Fragment, useCallback, useMemo, useState } from "react";
import {
  resolveTileAssetUrl,
  type PaintAsset,
  type PaintAssetAvailability,
  type PaintAvailability,
  type PaintKind,
  type PaintMode,
  type PaintSet,
  type PaintSetEntry,
  type TaxonomyNode,
  type TaxonomyNodePaintSet,
} from "@west-marches/shared";
import {
  PAINT_AVAILABILITY_LABELS,
  PAINT_MODE_LABELS,
  useAttachChildPaintSetToPaintSet,
  useAttachPaintAssetToPaintSet,
  useBulkMovePaintSets,
  useBulkSetPaintSetAvailability,
  useDeletePaintAsset,
  useDeletePaintSet,
  useDeleteTaxonomyNode,
  useDetachPaintSetEntry,
  useMergePaintSets,
  useMovePaintAssetBetweenSets,
  useMoveTaxonomyNode,
  usePaintCatalog,
  useReorderPaintFolderContents,
  useSetPaintSetAvailability,
  useSetPaintAssetAvailability,
  useSetPaintSetPaintMode,
  useSetTaxonomyNodeAvailability,
  useSplitPaintSet,
  useUpsertPaintSet,
  useUpsertTaxonomyNode,
} from "./usePaintCatalog";

const KIND_COPY: Record<PaintKind, { workspaceTitle: string; rootLabel: string; setNoun: string }> =
  {
    tile: { workspaceTitle: "System catalog", rootLabel: "All tile folders", setNoun: "tile set" },
    overlay: {
      workspaceTitle: "Decorations",
      rootLabel: "All decoration folders",
      setNoun: "decoration set",
    },
  };

const DND_MIME = "application/x-paint-set-ids";
const CONTENT_REORDER_MIME = "application/x-paint-folder-content-item";
const ASSET_ENTRY_MIME = "application/x-paint-set-asset-entry";

type DropPlacement = "before" | "after" | "merge" | "asset";
type DraggedContent = { kind: FolderContentItem["kind"]; id: string; paintSetIds: string[] };

interface DraggedAssetEntry {
  entryId: string;
  paintAssetId: string;
  sourcePaintSetId: string;
}

type FolderContentItem =
  | { kind: "folder"; id: string; sortOrder: number; node: TaxonomyNode }
  | {
      kind: "paint-set";
      id: string;
      sortOrder: number;
      set: PaintSet;
      link: TaxonomyNodePaintSet;
    };

interface PaintSetLinkEntry {
  link: TaxonomyNodePaintSet;
  set: PaintSet;
}

function compareCatalogRows(
  left: { sortOrder: number; label: string; kind: string; id: string },
  right: { sortOrder: number; label: string; kind: string; id: string },
): number {
  return (
    left.sortOrder - right.sortOrder ||
    left.label.localeCompare(right.label) ||
    left.kind.localeCompare(right.kind) ||
    left.id.localeCompare(right.id)
  );
}

function moveContentItem(
  items: FolderContentItem[],
  draggedId: string,
  targetId: string,
  placement: "before" | "after",
): FolderContentItem[] {
  if (draggedId === targetId) {
    return items;
  }
  const dragged = items.find((item) => item.id === draggedId);
  if (!dragged) {
    return items;
  }
  const withoutDragged = items.filter((item) => item.id !== draggedId);
  const targetIndex = withoutDragged.findIndex((item) => item.id === targetId);
  if (targetIndex === -1) {
    return items;
  }
  const insertIndex = placement === "before" ? targetIndex : targetIndex + 1;
  return [
    ...withoutDragged.slice(0, insertIndex),
    dragged,
    ...withoutDragged.slice(insertIndex),
  ];
}

function parseDraggedContent(raw: string): { kind: FolderContentItem["kind"]; id: string } | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (
      (parsed?.kind === "folder" || parsed?.kind === "paint-set") &&
      typeof parsed.id === "string"
    ) {
      return { kind: parsed.kind, id: parsed.id };
    }
  } catch {
    return null;
  }
  return null;
}

function parseDraggedPaintSetIds(raw: string): string[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function parseDraggedAssetEntry(raw: string): DraggedAssetEntry | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.entryId === "string" &&
      typeof parsed.paintAssetId === "string" &&
      typeof parsed.sourcePaintSetId === "string"
    ) {
      return {
        entryId: parsed.entryId,
        paintAssetId: parsed.paintAssetId,
        sourcePaintSetId: parsed.sourcePaintSetId,
      };
    }
  } catch {
    return null;
  }
  return null;
}

function getDropPlacement(
  event: React.DragEvent<HTMLElement>,
  targetItem: FolderContentItem,
  dragged: DraggedContent | null,
): DropPlacement {
  const rect = event.currentTarget.getBoundingClientRect();
  const relativeY = event.clientY - rect.top;
  const mergeCandidate =
    targetItem.kind === "paint-set" &&
    dragged?.kind === "paint-set" &&
    dragged.paintSetIds.some((id) => id !== targetItem.id);

  if (mergeCandidate && relativeY >= rect.height / 3 && relativeY <= (rect.height * 2) / 3) {
    return "merge";
  }

  return relativeY < rect.height / 2 ? "before" : "after";
}

function getPaintAssetPreviewUrl(asset: PaintAsset | undefined): string | null {
  if (!asset) {
    return null;
  }
  const directUrl = asset.assetUrl?.trim();
  if (directUrl) {
    return directUrl;
  }
  const storagePath = asset.storagePath?.trim();
  return storagePath ? resolveTileAssetUrl(storagePath) : null;
}

function buildFolderContentItems(
  parentId: string | null,
  childFoldersByParentId: Map<string | null, TaxonomyNode[]>,
  setEntriesByFolderId: Map<string | null, PaintSetLinkEntry[]>,
): FolderContentItem[] {
  return [
    ...(childFoldersByParentId.get(parentId) ?? []).map((node) => ({
      kind: "folder" as const,
      id: node.id,
      sortOrder: node.sortOrder,
      node,
    })),
    ...(setEntriesByFolderId.get(parentId) ?? []).map(({ link, set }) => ({
      kind: "paint-set" as const,
      id: set.id,
      sortOrder: link.sortOrder,
      link,
      set,
    })),
  ].sort((a, b) =>
    compareCatalogRows(
      {
        sortOrder: a.sortOrder,
        label: a.kind === "folder" ? a.node.label : a.set.title,
        kind: a.kind,
        id: a.id,
      },
      {
        sortOrder: b.sortOrder,
        label: b.kind === "folder" ? b.node.label : b.set.title,
        kind: b.kind,
        id: b.id,
      },
    ),
  );
}

// ---------------------------------------------------------------------------
// Toast system (in-house, no extra deps)
// ---------------------------------------------------------------------------

type ToastTone = "info" | "success" | "error";
interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, tone, message }]);
      window.setTimeout(() => dismiss(id), tone === "error" ? 6000 : 3000);
    },
    [dismiss],
  );
  return { toasts, push, dismiss };
}

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-50 flex flex-col gap-2">
      {toasts.map((toast) => {
        const palette =
          toast.tone === "error"
            ? "bg-red-50 border-red-700 text-red-900"
            : toast.tone === "success"
              ? "bg-emerald-50 border-emerald-700 text-emerald-900"
              : "bg-neutral-50 border-neutral-700 text-neutral-900";
        return (
          <div
            key={toast.id}
            role="status"
            className={`pointer-events-auto flex max-w-sm items-start gap-3 rounded-md border-[3px] px-4 py-3 text-sm shadow-[3px_3px_0_rgba(19,19,19,0.12)] ${palette}`}
          >
            <span className="flex-1">{toast.message}</span>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              className="text-xs underline"
            >
              dismiss
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface CatalogAdminProps {
  kind: PaintKind;
}

export default function CatalogAdmin({ kind }: CatalogAdminProps) {
  const copy = KIND_COPY[kind];
  const catalogQuery = usePaintCatalog(kind);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedSetIds, setSelectedSetIds] = useState<Set<string>>(new Set());
  const [expandedSetIds, setExpandedSetIds] = useState<Set<string>>(new Set());
  const [dragHoverFolderId, setDragHoverFolderId] = useState<string | null>(null);
  const [isRootDropTarget, setIsRootDropTarget] = useState(false);
  const [isOrphansView, setIsOrphansView] = useState(false);
  const [contentDropTarget, setContentDropTarget] = useState<{
    id: string;
    placement: DropPlacement;
  } | null>(null);
  const [draggedContent, setDraggedContent] = useState<DraggedContent | null>(null);
  const [draggedAssetEntry, setDraggedAssetEntry] = useState<DraggedAssetEntry | null>(null);

  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();

  const folderById = useMemo(() => {
    const map = new Map<string, TaxonomyNode>();
    for (const node of catalogQuery.data?.taxonomyNodes ?? []) {
      map.set(node.id, node);
    }
    return map;
  }, [catalogQuery.data?.taxonomyNodes]);

  const paintSetById = useMemo(() => {
    const map = new Map<string, PaintSet>();
    for (const set of catalogQuery.data?.paintSets ?? []) {
      map.set(set.id, set);
    }
    return map;
  }, [catalogQuery.data?.paintSets]);

  const paintAssetById = useMemo(() => {
    const map = new Map<string, PaintAsset>();
    for (const asset of catalogQuery.data?.paintAssets ?? []) {
      map.set(asset.id, asset);
    }
    return map;
  }, [catalogQuery.data?.paintAssets]);

  const childFoldersByParentId = useMemo(() => {
    const grouped = new Map<string | null, TaxonomyNode[]>();
    for (const node of catalogQuery.data?.taxonomyNodes ?? []) {
      const list = grouped.get(node.parentId) ?? [];
      list.push(node);
      grouped.set(node.parentId, list);
    }
    for (const list of grouped.values()) {
      list.sort((a, b) =>
        compareCatalogRows(
          { sortOrder: a.sortOrder, label: a.label, kind: "folder", id: a.id },
          { sortOrder: b.sortOrder, label: b.label, kind: "folder", id: b.id },
        ),
      );
    }
    return grouped;
  }, [catalogQuery.data?.taxonomyNodes]);

  const setEntriesByFolderId = useMemo(() => {
    const grouped = new Map<string | null, PaintSetLinkEntry[]>();
    for (const link of catalogQuery.data?.taxonomyNodePaintSets ?? []) {
      const set = paintSetById.get(link.paintSetId);
      if (!set) {
        continue;
      }
      const list = grouped.get(link.taxonomyNodeId) ?? [];
      list.push({ link, set });
      grouped.set(link.taxonomyNodeId, list);
    }
    for (const list of grouped.values()) {
      list.sort((a, b) =>
        compareCatalogRows(
          { sortOrder: a.link.sortOrder, label: a.set.title, kind: "paint-set", id: a.set.id },
          { sortOrder: b.link.sortOrder, label: b.set.title, kind: "paint-set", id: b.set.id },
        ),
      );
    }
    return grouped;
  }, [catalogQuery.data?.taxonomyNodePaintSets, paintSetById]);

  const rootTreeItems = useMemo<FolderContentItem[]>(
    () => buildFolderContentItems(null, childFoldersByParentId, setEntriesByFolderId),
    [childFoldersByParentId, setEntriesByFolderId],
  );
  const orphanItems = useMemo<FolderContentItem[]>(() => {
    const taxonomyNodes = catalogQuery.data?.taxonomyNodes ?? [];
    const folderIds = new Set(taxonomyNodes.map((node) => node.id));
    const reachableFolderIds = new Set<string>();
    const visitReachable = (nodeId: string) => {
      if (reachableFolderIds.has(nodeId)) {
        return;
      }
      reachableFolderIds.add(nodeId);
      for (const child of childFoldersByParentId.get(nodeId) ?? []) {
        visitReachable(child.id);
      }
    };
    for (const rootNode of childFoldersByParentId.get(null) ?? []) {
      visitReachable(rootNode.id);
    }
    const orphanFolders = (catalogQuery.data?.taxonomyNodes ?? [])
      .filter(
        (node) =>
          (node.parentId !== null && !folderIds.has(node.parentId)) ||
          !reachableFolderIds.has(node.id),
      )
      .map((node) => ({
        kind: "folder" as const,
        id: node.id,
        sortOrder: node.sortOrder,
        node,
      }));
    const orphanPaintSets = (catalogQuery.data?.taxonomyNodePaintSets ?? []).flatMap((link) => {
      if (link.taxonomyNodeId === null || folderIds.has(link.taxonomyNodeId)) {
        return [];
      }
      const set = paintSetById.get(link.paintSetId);
      return set
        ? [
            {
              kind: "paint-set" as const,
              id: set.id,
              sortOrder: link.sortOrder,
              link,
              set,
            },
          ]
        : [];
    });

    return [...orphanFolders, ...orphanPaintSets].sort((a, b) =>
      compareCatalogRows(
        {
          sortOrder: a.sortOrder,
          label: a.kind === "folder" ? a.node.label : a.set.title,
          kind: a.kind,
          id: a.id,
        },
        {
          sortOrder: b.sortOrder,
          label: b.kind === "folder" ? b.node.label : b.set.title,
          kind: b.kind,
          id: b.id,
        },
      ),
    );
  }, [
    childFoldersByParentId,
    catalogQuery.data?.taxonomyNodePaintSets,
    catalogQuery.data?.taxonomyNodes,
    paintSetById,
  ]);

  const assetsBySetId = useMemo(() => {
    const grouped = new Map<string, Array<{ entry: PaintSetEntry; asset: PaintAsset }>>();
    for (const entry of catalogQuery.data?.paintSetEntries ?? []) {
      if (entry.paintAssetId === null) {
        continue;
      }
      const asset = paintAssetById.get(entry.paintAssetId);
      if (!asset) continue;
      const list = grouped.get(entry.paintSetId) ?? [];
      list.push({ entry, asset });
      grouped.set(entry.paintSetId, list);
    }
    for (const list of grouped.values()) {
      list.sort((a, b) => a.entry.sortOrder - b.entry.sortOrder);
    }
    return grouped;
  }, [catalogQuery.data?.paintSetEntries, paintAssetById]);

  // Child paint_set entries grouped by parent paint_set id, used for nested
  // browsing in the per-set detail pane (e.g., Elevation -> Plains/Hills).
  const childSetEntriesBySetId = useMemo(() => {
    const setsById = new Map<string, PaintSet>();
    for (const set of catalogQuery.data?.paintSets ?? []) {
      setsById.set(set.id, set);
    }
    const grouped = new Map<string, Array<{ entry: PaintSetEntry; childSet: PaintSet }>>();
    for (const entry of catalogQuery.data?.paintSetEntries ?? []) {
      if (entry.childPaintSetId === null) {
        continue;
      }
      const childSet = setsById.get(entry.childPaintSetId);
      if (!childSet) continue;
      const list = grouped.get(entry.paintSetId) ?? [];
      list.push({ entry, childSet });
      grouped.set(entry.paintSetId, list);
    }
    for (const list of grouped.values()) {
      list.sort((a, b) => a.entry.sortOrder - b.entry.sortOrder);
    }
    return grouped;
  }, [catalogQuery.data?.paintSets, catalogQuery.data?.paintSetEntries]);

  const paintSetThumbnailUrlById = useMemo(() => {
    const cache = new Map<string, string | null>();
    const resolveForSet = (paintSetId: string, seen = new Set<string>()): string | null => {
      if (cache.has(paintSetId)) {
        return cache.get(paintSetId) ?? null;
      }
      if (seen.has(paintSetId)) {
        return null;
      }
      seen.add(paintSetId);

      const directAsset = assetsBySetId.get(paintSetId)?.[0]?.asset;
      const directUrl = getPaintAssetPreviewUrl(directAsset);
      if (directUrl) {
        cache.set(paintSetId, directUrl);
        return directUrl;
      }

      for (const { childSet } of childSetEntriesBySetId.get(paintSetId) ?? []) {
        const childUrl = resolveForSet(childSet.id, seen);
        if (childUrl) {
          cache.set(paintSetId, childUrl);
          return childUrl;
        }
      }

      cache.set(paintSetId, null);
      return null;
    };

    for (const set of catalogQuery.data?.paintSets ?? []) {
      resolveForSet(set.id);
    }
    return cache;
  }, [assetsBySetId, catalogQuery.data?.paintSets, childSetEntriesBySetId]);

  const folderThumbnailUrlById = useMemo(() => {
    const cache = new Map<string, string | null>();
    const resolveForFolder = (folderId: string, seen = new Set<string>()): string | null => {
      if (cache.has(folderId)) {
        return cache.get(folderId) ?? null;
      }
      if (seen.has(folderId)) {
        return null;
      }
      seen.add(folderId);

      const entries: FolderContentItem[] = [
        ...(childFoldersByParentId.get(folderId) ?? []).map((node) => ({
          kind: "folder" as const,
          id: node.id,
          sortOrder: node.sortOrder,
          node,
        })),
        ...(setEntriesByFolderId.get(folderId) ?? []).map(({ link, set }) => ({
          kind: "paint-set" as const,
          id: set.id,
          sortOrder: link.sortOrder,
          link,
          set,
        })),
      ].sort((a, b) =>
        compareCatalogRows(
          {
            sortOrder: a.sortOrder,
            label: a.kind === "folder" ? a.node.label : a.set.title,
            kind: a.kind,
            id: a.id,
          },
          {
            sortOrder: b.sortOrder,
            label: b.kind === "folder" ? b.node.label : b.set.title,
            kind: b.kind,
            id: b.id,
          },
        ),
      );

      for (const entry of entries) {
        const url =
          entry.kind === "folder"
            ? resolveForFolder(entry.id, seen)
            : (paintSetThumbnailUrlById.get(entry.id) ?? null);
        if (url) {
          cache.set(folderId, url);
          return url;
        }
      }

      cache.set(folderId, null);
      return null;
    };

    for (const node of catalogQuery.data?.taxonomyNodes ?? []) {
      resolveForFolder(node.id);
    }
    return cache;
  }, [
    catalogQuery.data?.taxonomyNodes,
    childFoldersByParentId,
    paintSetThumbnailUrlById,
    setEntriesByFolderId,
  ]);

  const folderContents = useMemo<FolderContentItem[]>(() => {
    if (!selectedFolderId) {
      return [];
    }
    return buildFolderContentItems(selectedFolderId, childFoldersByParentId, setEntriesByFolderId);
  }, [childFoldersByParentId, selectedFolderId, setEntriesByFolderId]);
  const activeContents = isOrphansView
    ? orphanItems
    : selectedFolderId === null
      ? rootTreeItems
      : folderContents;

  const upsertNode = useUpsertTaxonomyNode(kind);
  const moveNode = useMoveTaxonomyNode(kind);
  const deleteNode = useDeleteTaxonomyNode(kind);
  const setNodeAvail = useSetTaxonomyNodeAvailability(kind);
  const upsertSet = useUpsertPaintSet(kind);
  const deleteSet = useDeletePaintSet(kind);
  const setSetAvail = useSetPaintSetAvailability(kind);
  const setSetMode = useSetPaintSetPaintMode(kind);
  const splitSet = useSplitPaintSet(kind);
  const mergeSets = useMergePaintSets(kind);
  const bulkMove = useBulkMovePaintSets(kind);
  const bulkAvail = useBulkSetPaintSetAvailability(kind);
  const attachChildSet = useAttachChildPaintSetToPaintSet(kind);
  const attachAsset = useAttachPaintAssetToPaintSet(kind);
  const moveAsset = useMovePaintAssetBetweenSets(kind);
  const setAssetAvail = useSetPaintAssetAvailability(kind);
  const detachEntry = useDetachPaintSetEntry(kind);
  const deleteAsset = useDeletePaintAsset(kind);
  const reorderFolderContents = useReorderPaintFolderContents(kind);

  const runMutation = useCallback(
    async <T,>(promise: Promise<T>, successMessage?: string): Promise<T | null> => {
      try {
        const result = await promise;
        if (successMessage) pushToast("success", successMessage);
        return result;
      } catch (error) {
        const message =
          error instanceof Error ? error.message || "Request failed" : "Request failed";
        pushToast("error", message);
        return null;
      }
    },
    [pushToast],
  );

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectFolder = (id: string | null) => {
    setSelectedFolderId(id);
    setIsOrphansView(false);
    setSelectedSetIds(new Set());
    setExpandedSetIds(new Set());
  };

  const selectOrphans = () => {
    setSelectedFolderId(null);
    setIsOrphansView(true);
    setSelectedSetIds(new Set());
    setExpandedSetIds(new Set());
  };

  const selectPaintSetInTree = (paintSetId: string, folderId: string | null) => {
    setSelectedFolderId(folderId);
    setIsOrphansView(false);
    setSelectedSetIds(new Set([paintSetId]));
    setExpandedSetIds(new Set());
  };

  const toggleSetSelected = (id: string) =>
    setSelectedSetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSetExpanded = (id: string) =>
    setExpandedSetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ---- Folder actions ---------------------------------------------------

  const handleAddRootFolder = async () => {
    const label = window.prompt(`New top-level folder label`);
    if (!label?.trim()) return;
    await runMutation(
      upsertNode.mutateAsync({ kind, parentId: null, label: label.trim() }),
      "Folder created",
    );
  };

  const handleAddChildFolder = async (parent: TaxonomyNode) => {
    const label = window.prompt(`New child folder under "${parent.label}"`);
    if (!label?.trim()) return;
    await runMutation(
      upsertNode.mutateAsync({ kind, parentId: parent.id, label: label.trim() }),
      "Subfolder created",
    );
  };

  const handleRenameFolder = async (node: TaxonomyNode) => {
    const label = window.prompt(`Rename folder`, node.label);
    if (!label?.trim() || label.trim() === node.label) return;
    await runMutation(
      upsertNode.mutateAsync({ id: node.id, kind, label: label.trim() }),
      "Folder renamed",
    );
  };

  const handleSetFolderAvailability = async (
    node: TaxonomyNode,
    availability: PaintAvailability,
  ) => {
    await runMutation(
      setNodeAvail.mutateAsync({ id: node.id, kind, availability }),
      "Folder availability updated",
    );
  };

  const handleDeleteFolder = async (node: TaxonomyNode) => {
    if (!window.confirm(`Delete folder "${node.label}"? Child contents will move to Orphans.`)) return;
    const result = await runMutation(
      deleteNode.mutateAsync({ id: node.id, kind }),
      "Folder deleted",
    );
    if (result && selectedFolderId === node.id) {
      selectOrphans();
    }
  };

  // ---- Paint set actions ------------------------------------------------

  const handleCreateSetInFolder = async () => {
    if (isOrphansView) {
      return;
    }
    const title = window.prompt(`New ${copy.setNoun} title`);
    if (!title?.trim()) return;
    await runMutation(
      upsertSet.mutateAsync({
        kind,
        title: title.trim(),
        taxonomyNodeIds: [selectedFolderId],
      }),
      "Set created",
    );
  };

  const handleSetMode = async (set: PaintSet, mode: PaintMode) => {
    await runMutation(
      setSetMode.mutateAsync({ id: set.id, kind, paintMode: mode }),
      "Paint mode updated",
    );
  };

  const handleSetAvailability = async (set: PaintSet, availability: PaintAvailability) => {
    await runMutation(
      setSetAvail.mutateAsync({ id: set.id, kind, availability }),
      "Set availability updated",
    );
  };

  const handleRenameSet = async (set: PaintSet) => {
    const title = window.prompt("Rename set", set.title);
    if (!title?.trim() || title.trim() === set.title) return;
    await runMutation(
      upsertSet.mutateAsync({ id: set.id, kind, title: title.trim() }),
      "Set renamed",
    );
  };

  const handleSplitSet = async (set: PaintSet) => {
    if (!window.confirm(`Split "${set.title}" into one set per asset?`)) return;
    await runMutation(splitSet.mutateAsync({ paintSetId: set.id, kind }), "Set split");
  };

  const handleDeleteSet = async (set: PaintSet) => {
    if (!window.confirm(`Delete paint set "${set.title}"?\n\nThis cannot be undone.`)) return;
    await runMutation(deleteSet.mutateAsync({ id: set.id, kind }), "Set deleted");
    setSelectedSetIds((prev) => {
      const next = new Set(prev);
      next.delete(set.id);
      return next;
    });
  };

  const handleNestSetInParent = async (parentSet: PaintSet) => {
    const childPaintSetId = window.prompt(
      `Nest a ${copy.setNoun} inside "${parentSet.title}".\nEnter the child paint_set id:`,
    );
    if (!childPaintSetId?.trim()) return;
    const trimmed = childPaintSetId.trim();
    if (trimmed === parentSet.id) {
      pushToast("error", "A set cannot be nested inside itself");
      return;
    }
    const child = paintSetById.get(trimmed);
    if (!child) {
      pushToast("error", `No paint_set with id "${trimmed}"`);
      return;
    }
    if (child.kind !== parentSet.kind) {
      pushToast(
        "error",
        `Kind mismatch: parent is ${parentSet.kind}, child is ${child.kind}`,
      );
      return;
    }
    await runMutation(
      attachChildSet.mutateAsync({
        kind,
        parentPaintSetId: parentSet.id,
        childPaintSetId: trimmed,
      }),
      `Nested "${child.title}" inside "${parentSet.title}"`,
    );
  };

  const handleUnnestEntry = async (entry: PaintSetEntry, childSet: PaintSet) => {
    if (!window.confirm(`Unnest "${childSet.title}" from this set?`)) return;
    await runMutation(
      detachEntry.mutateAsync({ entryId: entry.id }),
      `Unnested "${childSet.title}"`,
    );
  };

  // ---- Asset actions ----------------------------------------------------

  const handleAddAssetToSet = async (set: PaintSet) => {
    const paintAssetId = window.prompt(
      `Add an asset to "${set.title}".\nEnter the paint_asset id:`,
    );
    if (!paintAssetId?.trim()) return;
    const trimmed = paintAssetId.trim();
    const asset = paintAssetById.get(trimmed);
    if (!asset) {
      pushToast("error", `No paint_asset with id "${trimmed}"`);
      return;
    }
    if (asset.kind !== set.kind) {
      pushToast("error", `Kind mismatch: set is ${set.kind}, asset is ${asset.kind}`);
      return;
    }
    await runMutation(
      attachAsset.mutateAsync({
        kind,
        paintAssetId: trimmed,
        paintSetId: set.id,
      }),
      `Added "${asset.title || trimmed}" to "${set.title}"`,
    );
  };

  const handleMoveAssetToSet = async (
    entry: PaintSetEntry,
    asset: PaintAsset,
    sourceSet: PaintSet,
  ) => {
    const destinationPaintSetId = window.prompt(
      `Move "${asset.title || asset.id}" out of "${sourceSet.title}".\nEnter the destination paint_set id:`,
    );
    if (!destinationPaintSetId?.trim()) return;
    const trimmed = destinationPaintSetId.trim();
    if (trimmed === sourceSet.id) {
      pushToast("error", "Destination is already the current set");
      return;
    }
    const destination = paintSetById.get(trimmed);
    if (!destination) {
      pushToast("error", `No paint_set with id "${trimmed}"`);
      return;
    }
    if (destination.kind !== sourceSet.kind) {
      pushToast("error", `Kind mismatch: source is ${sourceSet.kind}, destination is ${destination.kind}`);
      return;
    }
    if (!entry.paintAssetId) {
      pushToast("error", "Only asset entries can be moved with this action");
      return;
    }
    await runMutation(
      moveAsset.mutateAsync({
        kind,
        paintAssetId: entry.paintAssetId,
        sourcePaintSetId: sourceSet.id,
        destinationPaintSetId: trimmed,
      }),
      `Moved "${asset.title || asset.id}" to "${destination.title}"`,
    );
  };

  const moveAssetEntryToSet = async (
    dragged: DraggedAssetEntry,
    destinationSet: PaintSet,
  ): Promise<void> => {
    if (dragged.sourcePaintSetId === destinationSet.id) {
      pushToast("info", "Asset is already in that set");
      return;
    }
    const asset = paintAssetById.get(dragged.paintAssetId);
    const sourceSet = paintSetById.get(dragged.sourcePaintSetId);
    if (!asset) {
      pushToast("error", `No paint_asset with id "${dragged.paintAssetId}"`);
      return;
    }
    if (!sourceSet) {
      pushToast("error", `No source paint_set with id "${dragged.sourcePaintSetId}"`);
      return;
    }
    if (asset.kind !== destinationSet.kind || sourceSet.kind !== destinationSet.kind) {
      pushToast("error", "Kind mismatch moving asset");
      return;
    }
    await runMutation(
      moveAsset.mutateAsync({
        kind,
        paintAssetId: dragged.paintAssetId,
        sourcePaintSetId: dragged.sourcePaintSetId,
        destinationPaintSetId: destinationSet.id,
      }),
      `Moved "${asset.title || asset.id}" to "${destinationSet.title}"`,
    );
  };

  const handleRemoveAssetFromSet = async (
    entry: PaintSetEntry,
    asset: PaintAsset,
    sourceSet: PaintSet,
  ) => {
    if (!window.confirm(`Remove "${asset.title || asset.id}" from "${sourceSet.title}"?`)) return;
    await runMutation(
      detachEntry.mutateAsync({ entryId: entry.id }),
      `Removed "${asset.title || asset.id}" from "${sourceSet.title}"`,
    );
  };

  const handleSetAssetAvailability = async (
    asset: PaintAsset,
    availability: PaintAssetAvailability,
  ) => {
    await runMutation(
      setAssetAvail.mutateAsync({ id: asset.id, kind, availability }),
      `Asset marked ${PAINT_AVAILABILITY_LABELS[availability]}`,
    );
  };

  const handleDeleteAsset = async (asset: PaintAsset) => {
    const assetLabel = `${asset.namespace}:${asset.assetId}`;
    if (
      !window.confirm(
        `Delete asset "${asset.title || assetLabel}"?\n\nAsset id: ${assetLabel}\nThis cannot be undone.`,
      )
    ) {
      return;
    }
    await runMutation(deleteAsset.mutateAsync({ id: asset.id, kind }), "Asset deleted");
  };

  // ---- Bulk actions -----------------------------------------------------

  const selectedIdsArray = Array.from(selectedSetIds);

  const handleBulkMerge = async () => {
    if (selectedIdsArray.length < 2) return;
    const [target, ...rest] = selectedIdsArray;
    if (!target) return;
    if (!window.confirm(`Merge ${rest.length + 1} sets into the first selected?`)) return;
    await runMutation(
      mergeSets.mutateAsync({
        kind,
        targetPaintSetId: target,
        sourcePaintSetIds: rest,
      }),
      `Merged ${rest.length + 1} sets`,
    );
    setSelectedSetIds(new Set());
  };

  const handleBulkMoveTo = async (targetFolderId: string | null, idsOverride?: string[]) => {
    const ids = idsOverride ?? selectedIdsArray;
    if (ids.length === 0) return;
    if (targetFolderId !== null && !folderById.has(targetFolderId)) {
      pushToast("error", `No folder with id "${targetFolderId}"`);
      return;
    }
    await runMutation(
      bulkMove.mutateAsync({
        kind,
        paintSetIds: ids,
        targetTaxonomyNodeId: targetFolderId,
      }),
      targetFolderId === null
        ? `Moved ${ids.length} set${ids.length === 1 ? "" : "s"} to top level`
        : `Moved ${ids.length} set${ids.length === 1 ? "" : "s"} to "${folderById.get(targetFolderId)?.label}"`,
    );
    setSelectedSetIds(new Set());
  };

  const handleBulkMovePrompt = async () => {
    if (selectedIdsArray.length === 0) return;
    const targetFolderId = window.prompt(
      `Target folder id to move ${selectedIdsArray.length} sets into:`,
    );
    if (!targetFolderId?.trim()) return;
    await handleBulkMoveTo(targetFolderId.trim());
  };

  const handleBulkAvailability = async (availability: PaintAvailability) => {
    if (selectedIdsArray.length === 0) return;
    await runMutation(
      bulkAvail.mutateAsync({ kind, paintSetIds: selectedIdsArray, availability }),
      `Marked ${selectedIdsArray.length} set${selectedIdsArray.length === 1 ? "" : "s"} as ${PAINT_AVAILABILITY_LABELS[availability]}`,
    );
  };

  // ---- DnD --------------------------------------------------------------

  const handleFolderDragOver = (folderId: string, event: React.DragEvent<HTMLDivElement>) => {
    if (
      !event.dataTransfer.types.includes(DND_MIME) &&
      !event.dataTransfer.types.includes(CONTENT_REORDER_MIME)
    ) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (dragHoverFolderId !== folderId) setDragHoverFolderId(folderId);
  };

  const handleFolderDragLeave = (folderId: string) => {
    if (dragHoverFolderId === folderId) setDragHoverFolderId(null);
  };

  const isFolderDescendant = (folderId: string, possibleDescendantId: string): boolean => {
    const stack = [...(childFoldersByParentId.get(folderId) ?? [])];
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) {
        continue;
      }
      if (current.id === possibleDescendantId) {
        return true;
      }
      stack.push(...(childFoldersByParentId.get(current.id) ?? []));
    }
    return false;
  };

  const handleFolderDrop = async (folderId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragHoverFolderId(null);
    setDraggedContent(null);
    const raw = event.dataTransfer.getData(DND_MIME);
    if (!raw) {
      const contentRaw = event.dataTransfer.getData(CONTENT_REORDER_MIME);
      if (!contentRaw) return;
      let dragged: { kind?: unknown; id?: unknown };
      try {
        dragged = JSON.parse(contentRaw);
      } catch {
        return;
      }
      if (dragged.kind !== "folder" || typeof dragged.id !== "string") {
        return;
      }
      if (dragged.id === folderId || isFolderDescendant(dragged.id, folderId)) {
        pushToast("error", "A folder cannot be moved into itself or one of its subfolders");
        return;
      }
      await runMutation(
        moveNode.mutateAsync({
          id: dragged.id,
          kind,
          targetParentId: folderId,
        }),
        "Folder moved",
      );
      return;
    }
    let ids: string[];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      ids = parsed.filter((id): id is string => typeof id === "string");
    } catch {
      return;
    }
    if (ids.length === 0) return;
    await handleBulkMoveTo(folderId, ids);
  };

  const handleRootDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (
      !event.dataTransfer.types.includes(CONTENT_REORDER_MIME) &&
      !event.dataTransfer.types.includes(DND_MIME)
    ) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setIsRootDropTarget(true);
  };

  const handleRootDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsRootDropTarget(false);
    setDraggedContent(null);
    const paintSetRaw = event.dataTransfer.getData(DND_MIME);
    if (paintSetRaw) {
      let ids: string[];
      try {
        const parsed = JSON.parse(paintSetRaw);
        if (!Array.isArray(parsed)) return;
        ids = parsed.filter((id): id is string => typeof id === "string");
      } catch {
        return;
      }
      if (ids.length > 0) {
        await handleBulkMoveTo(null, ids);
      }
      return;
    }

    const raw = event.dataTransfer.getData(CONTENT_REORDER_MIME);
    if (!raw) {
      return;
    }

    let dragged: { kind?: unknown; id?: unknown };
    try {
      dragged = JSON.parse(raw);
    } catch {
      return;
    }

    if (typeof dragged.id !== "string") {
      return;
    }

    if (dragged.kind === "paint-set") {
      await handleBulkMoveTo(null, [dragged.id]);
      return;
    }

    if (dragged.kind === "folder") {
      await runMutation(
        moveNode.mutateAsync({
          id: dragged.id,
          kind,
          targetParentId: null,
        }),
        "Folder moved to top level",
      );
    }
  };

  const handleTreePaintSetDragStart = (set: PaintSet, event: React.DragEvent<HTMLElement>) => {
    const ids =
      selectedSetIds.has(set.id) && selectedSetIds.size > 0
        ? Array.from(selectedSetIds)
        : [set.id];
    setDraggedContent({ kind: "paint-set", id: set.id, paintSetIds: ids });
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(DND_MIME, JSON.stringify(ids));
    event.dataTransfer.setData(
      CONTENT_REORDER_MIME,
      JSON.stringify({ kind: "paint-set", id: set.id }),
    );
    event.dataTransfer.setData(
      "text/plain",
      `Move ${ids.length} ${kind} set${ids.length === 1 ? "" : "s"}`,
    );
  };

  const handleTreeFolderDragStart = (node: TaxonomyNode, event: React.DragEvent<HTMLElement>) => {
    setDraggedContent({ kind: "folder", id: node.id, paintSetIds: [] });
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      CONTENT_REORDER_MIME,
      JSON.stringify({ kind: "folder", id: node.id }),
    );
    event.dataTransfer.setData("text/plain", `Move folder ${node.label}`);
  };

  const handleContentDragStart = (
    item: FolderContentItem,
    event: React.DragEvent<HTMLLIElement>,
  ) => {
    const ids =
      item.kind === "paint-set"
        ? selectedSetIds.has(item.id) && selectedSetIds.size > 0
          ? Array.from(selectedSetIds)
          : [item.id]
        : [];
    setDraggedContent({ kind: item.kind, id: item.id, paintSetIds: ids });
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      CONTENT_REORDER_MIME,
      JSON.stringify({ kind: item.kind, id: item.id }),
    );
    if (item.kind === "paint-set") {
      event.dataTransfer.setData(DND_MIME, JSON.stringify(ids));
    }
    event.dataTransfer.setData("text/plain", `Move ${item.kind} ${item.id}`);
  };

  const handleContentDragEnd = () => {
    setContentDropTarget(null);
    setDraggedContent(null);
    setDraggedAssetEntry(null);
  };

  const handleAssetDragStart = (
    entry: PaintSetEntry,
    sourceSet: PaintSet,
    event: React.DragEvent<HTMLTableRowElement>,
  ) => {
    event.stopPropagation();
    if (!entry.paintAssetId) {
      return;
    }
    const dragged: DraggedAssetEntry = {
      entryId: entry.id,
      paintAssetId: entry.paintAssetId,
      sourcePaintSetId: sourceSet.id,
    };
    setDraggedAssetEntry(dragged);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(ASSET_ENTRY_MIME, JSON.stringify(dragged));
    event.dataTransfer.setData("text/plain", `Move asset ${entry.paintAssetId}`);
  };

  const handleContentDragOver = (
    targetItem: FolderContentItem,
    event: React.DragEvent<HTMLLIElement>,
  ) => {
    if (event.dataTransfer.types.includes(ASSET_ENTRY_MIME)) {
      if (targetItem.kind !== "paint-set") {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setContentDropTarget((current) =>
        current?.id === targetItem.id && current.placement === "asset"
          ? current
          : { id: targetItem.id, placement: "asset" },
      );
      return;
    }
    if (!event.dataTransfer.types.includes(CONTENT_REORDER_MIME)) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const transferDragged = parseDraggedContent(event.dataTransfer.getData(CONTENT_REORDER_MIME));
    const dragged = draggedContent ?? (
      transferDragged ? { ...transferDragged, paintSetIds: [] } : null
    );
    const placement = getDropPlacement(event, targetItem, dragged);
    setContentDropTarget((current) =>
      current?.id === targetItem.id && current.placement === placement
        ? current
        : { id: targetItem.id, placement },
    );
  };

  const handleContentDragLeave = (
    targetItem: FolderContentItem,
    event: React.DragEvent<HTMLLIElement>,
  ) => {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }
    setContentDropTarget((current) => (current?.id === targetItem.id ? null : current));
  };

  const handleContentDrop = async (
    targetItem: FolderContentItem,
    event: React.DragEvent<HTMLLIElement>,
  ) => {
    event.preventDefault();
    setContentDropTarget(null);
    setDraggedContent(null);
    const transferAsset = parseDraggedAssetEntry(event.dataTransfer.getData(ASSET_ENTRY_MIME));
    const assetDragged = draggedAssetEntry ?? transferAsset;
    setDraggedAssetEntry(null);
    if (assetDragged) {
      if (targetItem.kind === "paint-set") {
        await moveAssetEntryToSet(assetDragged, targetItem.set);
      }
      return;
    }
    if (isOrphansView) {
      return;
    }
    const transferDragged = parseDraggedContent(event.dataTransfer.getData(CONTENT_REORDER_MIME));
    const transferPaintSetIds = parseDraggedPaintSetIds(event.dataTransfer.getData(DND_MIME));
    const dragged = draggedContent ?? (
      transferDragged ? { ...transferDragged, paintSetIds: transferPaintSetIds } : null
    );
    if (!dragged) {
      return;
    }
    const dropPlacement = getDropPlacement(event, targetItem, dragged);
    const placement = contentDropTarget?.id === targetItem.id && contentDropTarget.placement === "merge"
      ? contentDropTarget.placement
      : dropPlacement;

    if (placement === "merge") {
      if (targetItem.kind !== "paint-set") {
        return;
      }
      const sourcePaintSetIds = (dragged.paintSetIds.length > 0 ? dragged.paintSetIds : [dragged.id]).filter(
        (id) => id !== targetItem.id,
      );
      if (dragged.kind !== "paint-set" || sourcePaintSetIds.length === 0) {
        return;
      }
      if (
        !window.confirm(
          `Merge ${sourcePaintSetIds.length} paint set${sourcePaintSetIds.length === 1 ? "" : "s"} into "${targetItem.set.title}"?`,
        )
      ) {
        return;
      }
      await runMutation(
        mergeSets.mutateAsync({
          kind,
          targetPaintSetId: targetItem.id,
          sourcePaintSetIds,
        }),
        `Merged ${sourcePaintSetIds.length} set${sourcePaintSetIds.length === 1 ? "" : "s"} into "${targetItem.set.title}"`,
      );
      setSelectedSetIds(new Set());
      return;
    }

    const nextItems = moveContentItem(activeContents, dragged.id, targetItem.id, placement);
    if (nextItems === activeContents) {
      return;
    }

    await runMutation(
      reorderFolderContents.mutateAsync({
        kind,
        taxonomyNodeId: selectedFolderId,
        items: nextItems.map((item) => ({
          kind: item.kind === "folder" ? "folder" : "paint-set",
          id: item.id,
        })),
      }),
      "Folder order updated",
    );
  };

  // ---- Render -----------------------------------------------------------

  const selectedFolder = selectedFolderId ? (folderById.get(selectedFolderId) ?? null) : null;

  return (
    <div className="flex min-h-screen flex-col bg-[#fffaf1] text-neutral-900">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b-[3px] border-neutral-950 px-8 py-6">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-neutral-500">
            Back office
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{copy.workspaceTitle}</h1>
          <p className="mt-1 text-xs text-neutral-600">
            Unified paint catalog · kind = {kind}
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddRootFolder}
          className="rounded-md border-[3px] border-neutral-950 bg-white px-4 py-2 text-sm font-semibold shadow-[3px_3px_0_rgba(19,19,19,0.12)] hover:bg-neutral-100"
        >
          + Add top-level folder
        </button>
      </header>

      {catalogQuery.isLoading ? (
        <div className="px-8 py-10 text-sm text-neutral-600">Loading catalog…</div>
      ) : catalogQuery.error ? (
        <div className="px-8 py-10 text-sm text-red-700">
          Failed to load catalog: {(catalogQuery.error as Error).message}
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-[320px_1fr] gap-0">
          <nav
            aria-label="Folder tree"
            className="border-r-[3px] border-neutral-950 bg-[#f6efe1] px-4 py-6"
          >
            <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-neutral-500">
              {copy.rootLabel}
            </p>
            <p className="mb-3 text-[10px] italic text-neutral-500">
              Tip: drag a set from the right onto a folder here to move it.
            </p>
            <button
              type="button"
              className={`mb-3 w-full rounded border-2 border-dashed px-3 py-2 text-left text-xs font-semibold ${
                isRootDropTarget
                  ? "border-emerald-700 bg-emerald-100 text-emerald-900"
                  : selectedFolderId === null && !isOrphansView
                    ? "border-amber-700 bg-amber-100 text-neutral-900"
                    : "border-neutral-400 bg-white/60 text-neutral-600 hover:bg-amber-50"
              }`}
              onClick={() => selectFolder(null)}
              onDragOver={handleRootDragOver}
              onDragLeave={() => setIsRootDropTarget(false)}
              onDrop={handleRootDrop}
            >
              Top Level
            </button>
            {rootTreeItems.length === 0 ? (
              <p className="text-sm text-neutral-600">No folders or sets yet.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {rootTreeItems.map((item) => (
                  <CatalogTreeRow
                    key={`${item.kind}:${item.id}`}
                    item={item}
                    depth={0}
                    expanded={expanded}
                    selectedFolderId={selectedFolderId}
                    selectedSetIds={selectedSetIds}
                    dragHoverFolderId={dragHoverFolderId}
                    childFoldersByParentId={childFoldersByParentId}
                    setEntriesByFolderId={setEntriesByFolderId}
                    folderThumbnailUrlById={folderThumbnailUrlById}
                    paintSetThumbnailUrlById={paintSetThumbnailUrlById}
                    onToggleExpanded={toggleExpanded}
                    onSelectFolder={selectFolder}
                    onSelectPaintSet={selectPaintSetInTree}
                    onDragOver={handleFolderDragOver}
                    onDragLeave={handleFolderDragLeave}
                    onDrop={handleFolderDrop}
                    onFolderDragStart={handleTreeFolderDragStart}
                    onPaintSetDragStart={handleTreePaintSetDragStart}
                  />
                ))}
              </ul>
            )}
            <button
              type="button"
              className={`mt-3 w-full rounded border-2 border-dashed px-3 py-2 text-left text-xs font-semibold ${
                isOrphansView
                  ? "border-amber-700 bg-amber-100 text-neutral-900"
                  : "border-neutral-400 bg-white/60 text-neutral-600 hover:bg-amber-50"
              }`}
              onClick={selectOrphans}
            >
              Orphans ({orphanItems.length})
            </button>
          </nav>

          <main className="px-8 py-6">
            {selectedFolderId === null || selectedFolder ? (
              <FolderDetail
                folder={selectedFolder}
                isOrphansView={isOrphansView}
                contents={activeContents}
                selectedSetIds={selectedSetIds}
                expandedSetIds={expandedSetIds}
                assetsBySetId={assetsBySetId}
                childSetEntriesBySetId={childSetEntriesBySetId}
                folderThumbnailUrlById={folderThumbnailUrlById}
                paintSetThumbnailUrlById={paintSetThumbnailUrlById}
                onToggleSetSelected={toggleSetSelected}
                onToggleSetExpanded={toggleSetExpanded}
                onSelectFolder={selectFolder}
                onClearSelection={() => setSelectedSetIds(new Set())}
                onAddRootFolder={handleAddRootFolder}
                onAddChildFolder={handleAddChildFolder}
                onRenameFolder={handleRenameFolder}
                onSetFolderAvailability={handleSetFolderAvailability}
                onDeleteFolder={handleDeleteFolder}
                onCreateSetInFolder={handleCreateSetInFolder}
                onRenameSet={handleRenameSet}
                onSetMode={handleSetMode}
                onSetAvailability={handleSetAvailability}
                onSplitSet={handleSplitSet}
                onDeleteSet={handleDeleteSet}
                onNestSet={handleNestSetInParent}
                onUnnestEntry={handleUnnestEntry}
                onAddAssetToSet={handleAddAssetToSet}
                onMoveAssetToSet={handleMoveAssetToSet}
                onRemoveAssetFromSet={handleRemoveAssetFromSet}
                onSetAssetAvailability={handleSetAssetAvailability}
                onDeleteAsset={handleDeleteAsset}
                onAssetDragStart={handleAssetDragStart}
                onBulkMerge={handleBulkMerge}
                onBulkMove={handleBulkMovePrompt}
                onBulkAvailability={handleBulkAvailability}
                contentDropTarget={contentDropTarget}
                onContentDragStart={handleContentDragStart}
                onContentDragEnd={handleContentDragEnd}
                onContentDragOver={handleContentDragOver}
                onContentDragLeave={handleContentDragLeave}
                onContentDrop={handleContentDrop}
                copy={copy}
              />
            ) : (
              <p className="text-sm text-neutral-600">Select a folder to view its contents.</p>
            )}
          </main>
        </div>
      )}

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Folder tree row
// ---------------------------------------------------------------------------

interface CatalogTreeRowProps {
  item: FolderContentItem;
  depth: number;
  expanded: Set<string>;
  selectedFolderId: string | null;
  selectedSetIds: Set<string>;
  dragHoverFolderId: string | null;
  childFoldersByParentId: Map<string | null, TaxonomyNode[]>;
  setEntriesByFolderId: Map<string | null, PaintSetLinkEntry[]>;
  folderThumbnailUrlById: Map<string, string | null>;
  paintSetThumbnailUrlById: Map<string, string | null>;
  onToggleExpanded: (id: string) => void;
  onSelectFolder: (id: string) => void;
  onSelectPaintSet: (paintSetId: string, folderId: string | null) => void;
  onDragOver: (folderId: string, event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (folderId: string) => void;
  onDrop: (folderId: string, event: React.DragEvent<HTMLDivElement>) => void;
  onFolderDragStart: (node: TaxonomyNode, event: React.DragEvent<HTMLElement>) => void;
  onPaintSetDragStart: (set: PaintSet, event: React.DragEvent<HTMLElement>) => void;
}

function CatalogTreeRow({
  item,
  depth,
  expanded,
  selectedFolderId,
  selectedSetIds,
  dragHoverFolderId,
  childFoldersByParentId,
  setEntriesByFolderId,
  folderThumbnailUrlById,
  paintSetThumbnailUrlById,
  onToggleExpanded,
  onSelectFolder,
  onSelectPaintSet,
  onDragOver,
  onDragLeave,
  onDrop,
  onFolderDragStart,
  onPaintSetDragStart,
}: CatalogTreeRowProps) {
  if (item.kind === "paint-set") {
    const isSelected = selectedSetIds.has(item.id);
    return (
      <li>
        <div
          draggable
          className={`flex items-center gap-2 rounded px-2 py-1 ${
            isSelected ? "bg-amber-100/80" : "hover:bg-amber-100/60"
          }`}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          onDragStart={(event) => onPaintSetDragStart(item.set, event)}
        >
          <span className="w-4 text-xs text-neutral-400" aria-hidden />
          <span className="cursor-grab select-none text-xs text-neutral-400" title="Drag onto a folder to move">
            ///
          </span>
          <span className="relative h-6 w-6 shrink-0 overflow-hidden rounded border border-neutral-950/15 bg-neutral-950/[0.035]">
            {paintSetThumbnailUrlById.get(item.id) ? (
              <img
                src={paintSetThumbnailUrlById.get(item.id) ?? ""}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                aria-hidden
                draggable={false}
                loading="lazy"
              />
            ) : null}
          </span>
          <button
            type="button"
            onClick={() => onSelectPaintSet(item.id, item.link.taxonomyNodeId)}
            className="min-w-0 flex-1 truncate text-left text-xs font-medium text-neutral-700"
          >
            {item.set.title}
          </button>
          <span className="rounded bg-neutral-200 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-neutral-600">
            Set
          </span>
        </div>
      </li>
    );
  }

  const node = item.node;
  const children = buildFolderContentItems(node.id, childFoldersByParentId, setEntriesByFolderId);
  const isExpanded = expanded.has(node.id);
  const isSelected = selectedFolderId === node.id;
  const isDropTarget = dragHoverFolderId === node.id;
  const hasChildren = children.length > 0;

  return (
    <li>
      <div
        className={`flex items-center gap-2 rounded px-2 py-1 ${
          isDropTarget
            ? "bg-emerald-200/80 ring-2 ring-emerald-700"
            : isSelected
              ? "bg-amber-200/70"
              : "hover:bg-amber-100/60"
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
        onDragOver={(event) => onDragOver(node.id, event)}
        onDragLeave={() => onDragLeave(node.id)}
        onDrop={(event) => onDrop(node.id, event)}
      >
        <button
          type="button"
          onClick={() => hasChildren && onToggleExpanded(node.id)}
          className="w-4 text-xs text-neutral-500"
          aria-label={hasChildren ? (isExpanded ? "Collapse" : "Expand") : "No children"}
        >
          {hasChildren ? (isExpanded ? "▾" : "▸") : ""}
        </button>
        <span
          draggable
          className="cursor-grab select-none text-xs text-neutral-400 active:cursor-grabbing"
          title="Drag folder"
          onDragStart={(event) => onFolderDragStart(node, event)}
        >
          ///
        </span>
        <span className="relative h-6 w-6 shrink-0 overflow-hidden rounded border border-neutral-950/15 bg-neutral-950/[0.035]">
          {folderThumbnailUrlById.get(node.id) ? (
            <img
              src={folderThumbnailUrlById.get(node.id) ?? ""}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              aria-hidden
              draggable={false}
              loading="lazy"
            />
          ) : null}
        </span>
        <button
          type="button"
          onClick={() => onSelectFolder(node.id)}
          className="min-w-0 flex-1 truncate text-left text-sm font-medium"
        >
          {node.label}
        </button>
        <SourceBadge source={node.source} />
        <AvailabilityBadge availability={node.availability} />
      </div>
      {hasChildren && isExpanded ? (
        <ul className="mt-1 flex flex-col gap-1">
          {children.map((child) => (
            <CatalogTreeRow
              key={`${child.kind}:${child.id}`}
              item={child}
              depth={depth + 1}
              expanded={expanded}
              selectedFolderId={selectedFolderId}
              selectedSetIds={selectedSetIds}
              dragHoverFolderId={dragHoverFolderId}
              childFoldersByParentId={childFoldersByParentId}
              setEntriesByFolderId={setEntriesByFolderId}
              folderThumbnailUrlById={folderThumbnailUrlById}
              paintSetThumbnailUrlById={paintSetThumbnailUrlById}
              onToggleExpanded={onToggleExpanded}
              onSelectFolder={onSelectFolder}
              onSelectPaintSet={onSelectPaintSet}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onFolderDragStart={onFolderDragStart}
              onPaintSetDragStart={onPaintSetDragStart}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Folder detail panel
// ---------------------------------------------------------------------------

interface FolderDetailProps {
  folder: TaxonomyNode | null;
  isOrphansView: boolean;
  contents: FolderContentItem[];
  selectedSetIds: Set<string>;
  expandedSetIds: Set<string>;
  assetsBySetId: Map<string, Array<{ entry: PaintSetEntry; asset: PaintAsset }>>;
  childSetEntriesBySetId: Map<string, Array<{ entry: PaintSetEntry; childSet: PaintSet }>>;
  folderThumbnailUrlById: Map<string, string | null>;
  paintSetThumbnailUrlById: Map<string, string | null>;
  onToggleSetSelected: (id: string) => void;
  onToggleSetExpanded: (id: string) => void;
  onSelectFolder: (id: string) => void;
  onClearSelection: () => void;
  onAddRootFolder: () => void;
  onAddChildFolder: (parent: TaxonomyNode) => void;
  onRenameFolder: (folder: TaxonomyNode) => void;
  onSetFolderAvailability: (folder: TaxonomyNode, availability: PaintAvailability) => void;
  onDeleteFolder: (folder: TaxonomyNode) => void;
  onCreateSetInFolder: () => void;
  onRenameSet: (set: PaintSet) => void;
  onSetMode: (set: PaintSet, mode: PaintMode) => void;
  onSetAvailability: (set: PaintSet, availability: PaintAvailability) => void;
  onSplitSet: (set: PaintSet) => void;
  onDeleteSet: (set: PaintSet) => void;
  onNestSet: (parent: PaintSet) => void;
  onUnnestEntry: (entry: PaintSetEntry, childSet: PaintSet) => void;
  onAddAssetToSet: (set: PaintSet) => void;
  onMoveAssetToSet: (entry: PaintSetEntry, asset: PaintAsset, sourceSet: PaintSet) => void;
  onRemoveAssetFromSet: (entry: PaintSetEntry, asset: PaintAsset, sourceSet: PaintSet) => void;
  onSetAssetAvailability: (asset: PaintAsset, availability: PaintAssetAvailability) => void;
  onDeleteAsset: (asset: PaintAsset) => void;
  onAssetDragStart: (
    entry: PaintSetEntry,
    sourceSet: PaintSet,
    event: React.DragEvent<HTMLTableRowElement>,
  ) => void;
  onBulkMerge: () => void;
  onBulkMove: () => void;
  onBulkAvailability: (availability: PaintAvailability) => void;
  contentDropTarget: { id: string; placement: DropPlacement } | null;
  onContentDragStart: (item: FolderContentItem, event: React.DragEvent<HTMLLIElement>) => void;
  onContentDragEnd: () => void;
  onContentDragOver: (item: FolderContentItem, event: React.DragEvent<HTMLLIElement>) => void;
  onContentDragLeave: (item: FolderContentItem, event: React.DragEvent<HTMLLIElement>) => void;
  onContentDrop: (targetItem: FolderContentItem, event: React.DragEvent<HTMLLIElement>) => void;
  copy: { workspaceTitle: string; rootLabel: string; setNoun: string };
}

function FolderDetail({
  folder,
  isOrphansView,
  contents,
  selectedSetIds,
  expandedSetIds,
  assetsBySetId,
  childSetEntriesBySetId,
  folderThumbnailUrlById,
  paintSetThumbnailUrlById,
  onToggleSetSelected,
  onToggleSetExpanded,
  onSelectFolder,
  onClearSelection,
  onAddRootFolder,
  onAddChildFolder,
  onRenameFolder,
  onSetFolderAvailability,
  onDeleteFolder,
  onCreateSetInFolder,
  onRenameSet,
  onSetMode,
  onSetAvailability,
  onSplitSet,
  onDeleteSet,
  onNestSet,
  onUnnestEntry,
  onAddAssetToSet,
  onMoveAssetToSet,
  onRemoveAssetFromSet,
  onSetAssetAvailability,
  onDeleteAsset,
  onAssetDragStart,
  onBulkMerge,
  onBulkMove,
  onBulkAvailability,
  contentDropTarget,
  onContentDragStart,
  onContentDragEnd,
  onContentDragOver,
  onContentDragLeave,
  onContentDrop,
  copy,
}: FolderDetailProps) {
  const isRoot = folder === null;
  const heading = isOrphansView ? "Orphans" : isRoot ? "Top Level" : folder.label;

  function getDropPlacementFor(item: FolderContentItem): DropPlacement | null {
    return contentDropTarget?.id === item.id ? contentDropTarget.placement : null;
  }

  return (
    <Fragment>
      <section className="mb-6 rounded-md border-[3px] border-neutral-950 bg-white p-5 shadow-[4px_4px_0_rgba(19,19,19,0.08)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">{heading}</h2>
            {folder ? (
              <p className="mt-1 text-xs text-neutral-500">
                id <code className="rounded bg-neutral-100 px-1">{folder.id}</code>
              </p>
            ) : isOrphansView ? (
              <p className="mt-1 text-xs text-neutral-500">
                Contents whose parent folder was deleted. Drag items into Top Level or another folder to restore them.
              </p>
            ) : (
              <p className="mt-1 text-xs text-neutral-500">
                Root folders and {copy.setNoun}s shown in the picker top level.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {folder ? (
              <>
                <SourceBadge source={folder.source} />
                <AvailabilityBadge availability={folder.availability} />
                <AvailabilityPicker
                  value={folder.availability}
                  onChange={(value) => onSetFolderAvailability(folder, value)}
                />
              </>
            ) : null}
            {!isOrphansView ? (
              <button
                type="button"
                onClick={() => (folder ? onAddChildFolder(folder) : onAddRootFolder())}
                className="rounded border-[3px] border-neutral-950 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-neutral-100"
              >
                {folder ? "+ Subfolder" : "+ Folder"}
              </button>
            ) : null}
            {folder ? (
              <>
                <button
                  type="button"
                  onClick={() => onRenameFolder(folder)}
                  title="Rename folder"
                  className="rounded border-[3px] border-neutral-950 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-neutral-100 disabled:opacity-50"
                >
                  Rename
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteFolder(folder)}
                  className="rounded border-[3px] border-red-700 bg-white px-3 py-1.5 text-xs font-semibold text-red-800 hover:bg-red-50 disabled:opacity-50"
                >
                  Delete
                </button>
              </>
            ) : null}
          </div>
        </div>
      </section>

      <section className="rounded-md border-[3px] border-neutral-950 bg-white p-5 shadow-[4px_4px_0_rgba(19,19,19,0.08)]">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold">Contents ({contents.length})</h3>
          {!isOrphansView ? (
            <button
              type="button"
              onClick={onCreateSetInFolder}
              className="rounded border-[3px] border-neutral-950 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-neutral-100"
            >
              + Add {copy.setNoun}
            </button>
          ) : null}
        </div>

        {selectedSetIds.size > 0 ? (
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded border-[3px] border-amber-600 bg-amber-50 px-3 py-2 text-xs">
            <span className="font-semibold">{selectedSetIds.size} selected</span>
            <button
              type="button"
              onClick={onBulkMerge}
              disabled={selectedSetIds.size < 2}
              className="rounded border-2 border-neutral-950 bg-white px-2 py-1 font-semibold disabled:opacity-50"
            >
              Merge into first
            </button>
            <button
              type="button"
              onClick={onBulkMove}
              className="rounded border-2 border-neutral-950 bg-white px-2 py-1 font-semibold"
            >
              Move…
            </button>
            <span className="ml-2">Make:</span>
            <button
              type="button"
              onClick={() => onBulkAvailability("available")}
              className="rounded border-2 border-emerald-700 bg-white px-2 py-1 font-semibold text-emerald-800"
            >
              Available
            </button>
            <button
              type="button"
              onClick={() => onBulkAvailability("unavailable")}
              className="rounded border-2 border-red-700 bg-white px-2 py-1 font-semibold text-red-800"
            >
              Unavailable
            </button>
            <button
              type="button"
              onClick={() => onBulkAvailability("inherit")}
              className="rounded border-2 border-neutral-700 bg-white px-2 py-1 font-semibold text-neutral-700"
            >
              Inherit
            </button>
            <button
              type="button"
              onClick={onClearSelection}
              className="ml-auto text-neutral-600 underline"
            >
              Clear selection
            </button>
          </div>
        ) : null}

        {contents.length === 0 ? (
          <p className="text-sm text-neutral-600">No folders or sets in this folder.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {contents.map((item) => {
              if (item.kind === "folder") {
                const isUnavailable = item.node.availability === "unavailable";
                const dropPlacement = getDropPlacementFor(item);
                return (
                  <li
                    key={`folder:${item.id}`}
                    draggable
                    onDragStart={(event) => onContentDragStart(item, event)}
                    onDragEnd={onContentDragEnd}
                    onDragOver={(event) => onContentDragOver(item, event)}
                    onDragLeave={(event) => onContentDragLeave(item, event)}
                    onDrop={(event) => onContentDrop(item, event)}
                    className={`relative rounded border-2 ${
                      isUnavailable
                        ? "border-red-200 bg-red-50/60"
                        : "border-neutral-300 bg-neutral-50"
                    } ${getContentDropClass(dropPlacement)}`}
                  >
                    <DropCue placement={dropPlacement} />
                    <div className="flex flex-wrap items-center gap-3 px-3 py-2">
                      <span
                        className="cursor-grab select-none text-sm text-neutral-400"
                        title="Drag to reorder"
                      >
                        ///
                      </span>
                      <ContentThumbnail thumbnailUrl={folderThumbnailUrlById.get(item.id) ?? null} />
                      <div className="min-w-48 flex-1">
                        <div className="text-sm font-semibold">{item.node.label}</div>
                        <div className="text-[10px] uppercase tracking-wide text-neutral-500">
                          folder · id <code className="rounded bg-neutral-100 px-1">{item.node.id}</code>
                        </div>
                      </div>
                      <SourceBadge source={item.node.source} />
                      <AvailabilityBadge availability={item.node.availability} />
                      <button
                        type="button"
                        onClick={() => onSelectFolder(item.node.id)}
                        className="rounded border border-neutral-400 bg-white px-2 py-1 text-xs hover:bg-neutral-100"
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => onRenameFolder(item.node)}
                        title="Rename folder"
                        className="rounded border border-neutral-400 bg-white px-2 py-1 text-xs disabled:opacity-50"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteFolder(item.node)}
                        className="rounded border border-red-700 bg-white px-2 py-1 text-xs text-red-800 disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                );
              }

              const set = item.set;
              const dropPlacement = getDropPlacementFor(item);
              return (
                <PaintSetRow
                  key={`paint-set:${set.id}`}
                  set={set}
                  selected={selectedSetIds.has(set.id)}
                  expanded={expandedSetIds.has(set.id)}
                  dropPlacement={dropPlacement}
                  thumbnailUrl={paintSetThumbnailUrlById.get(set.id) ?? null}
                  assets={assetsBySetId.get(set.id) ?? []}
                  childSetEntries={childSetEntriesBySetId.get(set.id) ?? []}
                  onToggleSelected={() => onToggleSetSelected(set.id)}
                  onToggleExpanded={() => onToggleSetExpanded(set.id)}
                  onRename={() => onRenameSet(set)}
                  onSetMode={(mode) => onSetMode(set, mode)}
                  onSetAvailability={(availability) => onSetAvailability(set, availability)}
                  onSplit={() => onSplitSet(set)}
                  onDelete={() => onDeleteSet(set)}
                  onNest={() => onNestSet(set)}
                  onUnnestEntry={onUnnestEntry}
                  onAddAsset={() => onAddAssetToSet(set)}
                  onMoveAsset={(entry, asset) => onMoveAssetToSet(entry, asset, set)}
                  onRemoveAsset={(entry, asset) => onRemoveAssetFromSet(entry, asset, set)}
                  onSetAssetAvailability={onSetAssetAvailability}
                  onDeleteAsset={onDeleteAsset}
                  onAssetDragStart={(entry, event) => onAssetDragStart(entry, set, event)}
                  onDragStart={(event) => onContentDragStart(item, event)}
                  onDragEnd={onContentDragEnd}
                  onDragOver={(event) => onContentDragOver(item, event)}
                  onDragLeave={(event) => onContentDragLeave(item, event)}
                  onDrop={(event) => onContentDrop(item, event)}
                />
              );
            })}
          </ul>
        )}
      </section>
    </Fragment>
  );
}

// ---------------------------------------------------------------------------
// Paint set row
// ---------------------------------------------------------------------------

function getContentDropClass(placement: DropPlacement | null): string {
  if (placement === "before") {
    return "border-t-4 border-t-sky-700";
  }
  if (placement === "after") {
    return "border-b-4 border-b-sky-700";
  }
  if (placement === "merge") {
    return "border-sky-700 bg-sky-50 ring-4 ring-sky-200";
  }
  if (placement === "asset") {
    return "border-emerald-700 bg-emerald-50 ring-4 ring-emerald-200";
  }
  return "";
}

function DropCue({ placement }: { placement: DropPlacement | null }) {
  if (!placement) {
    return null;
  }

  const label =
    placement === "before"
      ? "Drop above"
      : placement === "after"
        ? "Drop below"
        : placement === "merge"
          ? "Drop to merge"
          : "Drop asset here";
  const positionClass =
    placement === "before"
      ? "-top-3 left-4"
      : placement === "after"
        ? "-bottom-3 left-4"
        : "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2";

  return (
    <span
      className={`pointer-events-none absolute z-10 rounded-full border-2 border-sky-800 bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sky-950 shadow-sm ${positionClass}`}
    >
      {label}
    </span>
  );
}

function ContentThumbnail({ thumbnailUrl }: { thumbnailUrl: string | null }) {
  return (
    <span className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-neutral-950/20 bg-neutral-950/[0.035]">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          aria-hidden
          loading="lazy"
        />
      ) : null}
    </span>
  );
}

interface PaintSetRowProps {
  set: PaintSet;
  selected: boolean;
  expanded: boolean;
  dropPlacement: DropPlacement | null;
  thumbnailUrl: string | null;
  assets: Array<{ entry: PaintSetEntry; asset: PaintAsset }>;
  childSetEntries: Array<{ entry: PaintSetEntry; childSet: PaintSet }>;
  onToggleSelected: () => void;
  onToggleExpanded: () => void;
  onRename: () => void;
  onSetMode: (mode: PaintMode) => void;
  onSetAvailability: (availability: PaintAvailability) => void;
  onSplit: () => void;
  onDelete: () => void;
  onNest: () => void;
  onUnnestEntry: (entry: PaintSetEntry, childSet: PaintSet) => void;
  onAddAsset: () => void;
  onMoveAsset: (entry: PaintSetEntry, asset: PaintAsset) => void;
  onRemoveAsset: (entry: PaintSetEntry, asset: PaintAsset) => void;
  onSetAssetAvailability: (asset: PaintAsset, availability: PaintAssetAvailability) => void;
  onDeleteAsset: (asset: PaintAsset) => void;
  onAssetDragStart: (
    entry: PaintSetEntry,
    event: React.DragEvent<HTMLTableRowElement>,
  ) => void;
  onDragStart: (event: React.DragEvent<HTMLLIElement>) => void;
  onDragEnd: () => void;
  onDragOver: (event: React.DragEvent<HTMLLIElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLLIElement>) => void;
  onDrop: (event: React.DragEvent<HTMLLIElement>) => void;
}

function PaintSetRow({
  set,
  selected,
  expanded,
  dropPlacement,
  thumbnailUrl,
  assets,
  childSetEntries,
  onToggleSelected,
  onToggleExpanded,
  onRename,
  onSetMode,
  onSetAvailability,
  onSplit,
  onDelete,
  onNest,
  onUnnestEntry,
  onAddAsset,
  onMoveAsset,
  onRemoveAsset,
  onSetAssetAvailability,
  onDeleteAsset,
  onAssetDragStart,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}: PaintSetRowProps) {
  const isSystem = set.source === "system";
  const isUnavailable = set.availability === "unavailable";
  const protectedTitle = isSystem ? "Protected: source = system" : undefined;
  const totalEntries = assets.length + childSetEntries.length;

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`relative rounded border-2 ${
        selected
          ? "border-amber-600 bg-amber-50"
          : isUnavailable
            ? "border-red-200 bg-red-50/60"
            : "border-neutral-300 bg-neutral-50"
      } ${getContentDropClass(dropPlacement)}`}
    >
      <DropCue placement={dropPlacement} />
      <div className="flex flex-wrap items-center gap-3 px-3 py-2">
        <span
          className="cursor-grab select-none text-sm text-neutral-400"
          title="Drag onto a folder to move"
        >
          ⠿
        </span>
        <ContentThumbnail thumbnailUrl={thumbnailUrl} />
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelected}
          aria-label={`Select set ${set.title}`}
        />
        <button
          type="button"
          onClick={onToggleExpanded}
          className="w-4 text-xs text-neutral-500"
          aria-label={expanded ? "Collapse assets" : "Expand assets"}
        >
          {expanded ? "▾" : "▸"}
        </button>
        <div className="min-w-48 flex-1">
          <div className="text-sm font-semibold">{set.title}</div>
          <div className="text-[10px] uppercase tracking-wide text-neutral-500">
            id <code className="rounded bg-neutral-100 px-1">{set.id}</code> · {assets.length}{" "}
            asset{assets.length === 1 ? "" : "s"}
            {childSetEntries.length > 0
              ? ` · ${childSetEntries.length} nested set${childSetEntries.length === 1 ? "" : "s"}`
              : ""}
          </div>
        </div>
        <SourceBadge source={set.source} />
        <AvailabilityBadge availability={set.availability} />
        <select
          value={set.paintMode}
          onChange={(event) => onSetMode(event.target.value as PaintMode)}
          title="Set paint behavior"
          className="rounded border border-neutral-400 bg-white px-2 py-1 text-xs disabled:opacity-50"
        >
          {(Object.keys(PAINT_MODE_LABELS) as PaintMode[]).map((mode) => (
            <option key={mode} value={mode}>
              {PAINT_MODE_LABELS[mode]}
            </option>
          ))}
        </select>
        <AvailabilityPicker
          value={set.availability}
          disabled={isSystem}
          title={protectedTitle}
          onChange={onSetAvailability}
        />
        <button
          type="button"
          onClick={onRename}
          title="Rename set"
          className="rounded border border-neutral-400 bg-white px-2 py-1 text-xs disabled:opacity-50"
        >
          Rename
        </button>
        <button
          type="button"
          onClick={onSplit}
          disabled={isSystem}
          title={protectedTitle}
          className="rounded border border-neutral-400 bg-white px-2 py-1 text-xs disabled:opacity-50"
        >
          Split
        </button>
        <button
          type="button"
          onClick={onNest}
          disabled={isSystem}
          title={protectedTitle ?? "Nest another set inside this one"}
          className="rounded border border-neutral-400 bg-white px-2 py-1 text-xs disabled:opacity-50"
        >
          Nest in…
        </button>
        <button
          type="button"
          onClick={onAddAsset}
          title="Add an existing asset to this set"
          className="rounded border border-neutral-400 bg-white px-2 py-1 text-xs disabled:opacity-50"
        >
          Add asset…
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Delete this set"
          className="rounded border border-red-700 bg-white px-2 py-1 text-xs text-red-800 disabled:opacity-50"
        >
          Delete
        </button>
      </div>

      {expanded ? (
        <div className="flex flex-col gap-3 border-t border-neutral-300 bg-white px-3 py-3">
          {totalEntries === 0 ? (
            <p className="text-xs text-neutral-500">No assets or nested sets in this set.</p>
          ) : null}

          {childSetEntries.length > 0 ? (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                Nested paint sets
              </p>
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="py-1">#</th>
                    <th className="py-1">Title</th>
                    <th className="py-1">Set id</th>
                    <th className="py-1">Mode</th>
                    <th className="py-1">Source</th>
                    <th className="py-1 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {childSetEntries.map(({ entry, childSet }) => {
                    const childIsSystem = childSet.source === "system";
                    return (
                      <tr key={entry.id} className="border-t border-neutral-200">
                        <td className="py-1 text-neutral-500">{entry.sortOrder}</td>
                        <td className="py-1 font-medium">{childSet.title}</td>
                        <td className="py-1 text-neutral-600">
                          <code className="rounded bg-neutral-100 px-1">{childSet.id}</code>
                        </td>
                        <td className="py-1 text-neutral-600">
                          {PAINT_MODE_LABELS[childSet.paintMode]}
                        </td>
                        <td className="py-1">
                          <SourceBadge source={childSet.source} />
                        </td>
                        <td className="py-1 text-right">
                          <button
                            type="button"
                            onClick={() => onUnnestEntry(entry, childSet)}
                            disabled={isSystem || childIsSystem}
                            title={
                              isSystem || childIsSystem
                                ? "Protected: source = system"
                                : "Unnest this set"
                            }
                            className="rounded border border-neutral-700 bg-white px-2 py-1 text-[10px] text-neutral-800 disabled:opacity-50"
                          >
                            Unnest
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {assets.length > 0 ? (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                Assets
              </p>
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="py-1">#</th>
                    <th className="py-1">Title</th>
                    <th className="py-1">Asset id</th>
                    <th className="py-1">Availability</th>
                    <th className="py-1">Source</th>
                    <th className="py-1 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map(({ entry, asset }) => {
                    return (
                      <tr
                        key={asset.id}
                        draggable
                        onDragStart={(event) => onAssetDragStart(entry, event)}
                        onDragEnd={onDragEnd}
                        className={`cursor-grab border-t active:cursor-grabbing ${
                          asset.availability === "unavailable"
                            ? "border-red-200 bg-red-50/70"
                            : "border-neutral-200"
                        }`}
                      >
                        <td className="py-1 text-neutral-500">{entry.sortOrder}</td>
                        <td className="py-1 font-medium">{asset.title || "(untitled)"}</td>
                        <td className="py-1 text-neutral-600">
                          <code className="rounded bg-neutral-100 px-1">
                            {asset.namespace}:{asset.assetId}
                          </code>
                        </td>
                        <td className="py-1">
                          <select
                            value={asset.availability}
                            onChange={(event) =>
                              onSetAssetAvailability(
                                asset,
                                event.target.value as PaintAssetAvailability,
                              )
                            }
                            className="rounded border border-neutral-400 bg-white px-2 py-1 text-[10px]"
                          >
                            <option value="available">Available</option>
                            <option value="unavailable">Unavailable</option>
                          </select>
                        </td>
                        <td className="py-1">
                          <SourceBadge source={asset.source} />
                        </td>
                        <td className="py-1 text-right">
                          <button
                            type="button"
                            onClick={() => onMoveAsset(entry, asset)}
                            title="Move this asset to another set"
                            className="mr-1 rounded border border-neutral-700 bg-white px-2 py-1 text-[10px] text-neutral-800 disabled:opacity-50"
                          >
                            Move…
                          </button>
                          <button
                            type="button"
                            onClick={() => onRemoveAsset(entry, asset)}
                            disabled={isSystem}
                            title={isSystem ? "Protected: source = system" : "Remove this asset from this set"}
                            className="mr-1 rounded border border-neutral-700 bg-white px-2 py-1 text-[10px] text-neutral-800 disabled:opacity-50"
                          >
                            Remove
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteAsset(asset)}
                            title="Delete this asset"
                            className="rounded border border-red-700 bg-white px-2 py-1 text-[10px] text-red-800 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Badges & misc
// ---------------------------------------------------------------------------

function SourceBadge({ source }: { source: TaxonomyNode["source"] }) {
  const isSystem = source === "system";
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        isSystem ? "bg-neutral-900 text-white" : "bg-neutral-200 text-neutral-700"
      }`}
    >
      {isSystem ? "System" : "Community"}
    </span>
  );
}

function AvailabilityBadge({ availability }: { availability: PaintAvailability }) {
  const palette: Record<PaintAvailability, string> = {
    available: "bg-emerald-100 text-emerald-800 border border-emerald-700",
    unavailable: "bg-red-100 text-red-800 border border-red-700",
    inherit: "bg-neutral-100 text-neutral-700 border border-neutral-500",
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${palette[availability]}`}
    >
      {PAINT_AVAILABILITY_LABELS[availability]}
    </span>
  );
}

function AvailabilityPicker({
  value,
  onChange,
  disabled,
  title,
}: {
  value: PaintAvailability;
  onChange: (value: PaintAvailability) => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value as PaintAvailability)}
      disabled={disabled}
      title={title}
      className="rounded border border-neutral-400 bg-white px-2 py-1 text-xs disabled:opacity-50"
    >
      {(Object.keys(PAINT_AVAILABILITY_LABELS) as PaintAvailability[]).map((availability) => (
        <option key={availability} value={availability}>
          {PAINT_AVAILABILITY_LABELS[availability]}
        </option>
      ))}
    </select>
  );
}
